package com.schoolconnect.app.feature.itusers.add

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntRect
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.unit.LayoutDirection
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import androidx.compose.ui.window.Popup
import androidx.compose.ui.window.PopupPositionProvider
import androidx.compose.ui.window.PopupProperties
import com.schoolconnect.app.designsystem.AppDialogActionStyle
import com.schoolconnect.app.designsystem.AppDialogColors
import com.schoolconnect.app.designsystem.AppDialogFieldColumn
import com.schoolconnect.app.designsystem.AppDialogFooter
import com.schoolconnect.app.designsystem.AppDialogFooterAction
import com.schoolconnect.app.designsystem.AppDialogFullNameField
import com.schoolconnect.app.designsystem.AppDialogFormGrid
import com.schoolconnect.app.designsystem.AppDialogHeader
import com.schoolconnect.app.designsystem.AppDialogMobileField
import com.schoolconnect.app.designsystem.AppDialogSelectField
import com.schoolconnect.app.designsystem.AppDialogScaffold
import com.schoolconnect.app.designsystem.AppDialogStandardWidth
import com.schoolconnect.app.designsystem.AppDialogSize
import com.schoolconnect.app.designsystem.AppDialogTextField
import com.schoolconnect.app.designsystem.AppDialogValidationSummary
import com.schoolconnect.app.designsystem.AppDialogErrorMessage
import com.schoolconnect.app.feature.itusers.ITUsersBorder
import com.schoolconnect.app.feature.itusers.ITUsersDanger
import com.schoolconnect.app.feature.itusers.ITUsersInputBorder
import com.schoolconnect.app.feature.itusers.ITUsersMuted
import com.schoolconnect.app.feature.itusers.ITUsersNavy
import com.schoolconnect.app.feature.itusers.ITUsersPrimary
import com.schoolconnect.app.feature.itusers.ITUserFormValues
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.chevron_down
import ischoolconnect.sharedui.generated.resources.close_x
import ischoolconnect.sharedui.generated.resources.user_plus
import org.jetbrains.compose.resources.painterResource

private val AddITUserOverlay = Color(0x730F172A)
private val AddITUserSurface = Color.White
private val AddITUserIconTileBg = Color(0xFFEAF2FF)
private val AddITUserRoleOptionPurple = Color(0xFFEFE7FF)
private val AddITUserRoleOptionBlue = Color(0xFFEAF2FF)
private val AddITUserRoleOptionGreen = Color(0xFFE8F8EF)
private val AddITUserDialogColors = com.schoolconnect.app.designsystem.AppDialogDefaultColors


@Composable
internal fun AddITUserModal(
    open: Boolean,
    title: String,
    subtitle: String,
    initialValues: ITUserFormValues,
    onClose: () -> Unit,
    onSave: (ITUserFormValues) -> Unit,
    saveLabel: String,
    isSaving: Boolean = false,
    apiError: String = ""
) {
    if (!open) return

    var fullName by remember(initialValues) { mutableStateOf(initialValues.name) }
    var mobileNumber by remember(initialValues) { mutableStateOf(initialValues.mobile) }
    var emailAddress by remember(initialValues) { mutableStateOf(initialValues.email) }
    var role by remember(initialValues) { mutableStateOf(initialValues.role) }
    var status by remember(initialValues) { mutableStateOf(initialValues.status.ifBlank { "Active" }) }
    var hasSubmitted by remember { mutableStateOf(false) }
    var roleExpanded by remember { mutableStateOf(false) }
    var localSaving by remember { mutableStateOf(false) }

    val validation = remember(hasSubmitted, fullName, mobileNumber, emailAddress, role, status) {
        if (hasSubmitted) {
            validateAddITUser(
                ITUserFormValues(
                    name = fullName,
                    mobile = mobileNumber,
                    email = emailAddress,
                    role = role,
                    status = status
                )
            )
        } else {
            null
        }
    }
    val effectiveSaving = isSaving || localSaving

    LaunchedEffect(open, initialValues) {
        if (open) {
            fullName = initialValues.name
            mobileNumber = initialValues.mobile
            emailAddress = initialValues.email
            role = initialValues.role
            status = initialValues.status.ifBlank { "Active" }
            hasSubmitted = false
            roleExpanded = false
        }
    }

    AppDialogScaffold(
        onDismiss = { if (!effectiveSaving) onClose() },
        dismissOnClickOutside = false,
        size = AppDialogSize.Standard,
        colors = AddITUserDialogColors,
        maxDialogHeight = 900.dp,
        isLoading = effectiveSaving,
        loadingTitle = "Saving IT user"
    ) {
        AppDialogFormGrid(
            modifier = Modifier.fillMaxWidth(),
            contentAlignment = Alignment.Center,
            compactBreakpoint = 680.dp
        ) {
            val compact = it
            val innerCompact = compact
            val scrollState = rememberScrollState()

            Column(modifier = Modifier.fillMaxWidth()) {
                AddITUserHeader(
                    title = title,
                    subtitle = subtitle,
                    onClose = { if (!effectiveSaving) onClose() }
                )
                AppDialogValidationSummary(
                    errors = listOf(
                        validation?.name.orEmpty(),
                        validation?.mobile.orEmpty(),
                        validation?.email.orEmpty(),
                        validation?.role.orEmpty(),
                        validation?.status.orEmpty()
                    ),
                    colors = AddITUserDialogColors,
                    modifier = Modifier.padding(horizontal = 28.dp, vertical = 12.dp)
                )
                AppDialogErrorMessage(
                    message = apiError,
                    colors = AddITUserDialogColors,
                    modifier = Modifier.padding(horizontal = 28.dp, vertical = 12.dp)
                )

                HorizontalDivider(color = ITUsersBorder)

                BoxWithConstraints(
                    modifier = Modifier
                        .fillMaxWidth()
                        .weight(1f, fill = false)
                ) {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .verticalScroll(scrollState)
                            .padding(horizontal = if (innerCompact) 24.dp else 28.dp, vertical = 24.dp),
                        verticalArrangement = Arrangement.spacedBy(24.dp)
                    ) {
                        if (innerCompact) {
                            Column(verticalArrangement = Arrangement.spacedBy(18.dp)) {
                                AddITUserFieldColumn(
                                    label = "Full Name",
                                    required = true,
                                    error = validation?.name.orEmpty()
                                ) {
                                    AddITUserTextField(
                                        value = fullName,
                                        onValueChange = { fullName = it },
                                        placeholder = "Enter full name",
                                        error = validation?.name.orEmpty(),
                                        keyboardType = KeyboardType.Text
                                    )
                                }
                                AddITUserFieldColumn(
                                    label = "Mobile Number",
                                    required = true,
                                    error = validation?.mobile.orEmpty()
                                ) {
                                    AddITUserTextField(
                                        value = mobileNumber,
                                        onValueChange = { mobileNumber = it },
                                        placeholder = "Enter mobile number",
                                        error = validation?.mobile.orEmpty(),
                                        keyboardType = KeyboardType.Phone
                                    )
                                }
                                AddITUserFieldColumn(
                                    label = "Email Address",
                                    required = true,
                                    error = validation?.email.orEmpty()
                                ) {
                                    AddITUserTextField(
                                        value = emailAddress,
                                        onValueChange = { emailAddress = it },
                                        placeholder = "Enter email address",
                                        error = validation?.email.orEmpty(),
                                        keyboardType = KeyboardType.Email
                                    )
                                }
                                AddITUserFieldColumn(
                                    label = "Role",
                                    required = true,
                                    error = validation?.role.orEmpty()
                                ) {
                                    AddITUserRoleField(
                                        value = role,
                                        expanded = roleExpanded,
                                        onToggleExpanded = {
                                            roleExpanded = !roleExpanded
                                        },
                                        onDismissExpanded = { roleExpanded = false },
                                        onSelected = {
                                            role = it
                                            roleExpanded = false
                                        },
                                        error = validation?.role.orEmpty()
                                    )
                                }
                            }
                        } else {
                            Column(verticalArrangement = Arrangement.spacedBy(24.dp)) {
                                Row(horizontalArrangement = Arrangement.spacedBy(24.dp)) {
                                    AddITUserFieldColumn(
                                        modifier = Modifier.weight(1f),
                                        label = "Full Name",
                                        required = true,
                                        error = validation?.name.orEmpty()
                                    ) {
                                        AddITUserTextField(
                                            value = fullName,
                                            onValueChange = { fullName = it },
                                            placeholder = "Enter full name",
                                            error = validation?.name.orEmpty(),
                                            keyboardType = KeyboardType.Text
                                        )
                                    }
                                    AddITUserFieldColumn(
                                        modifier = Modifier.weight(1f),
                                        label = "Role",
                                        required = true,
                                        error = validation?.role.orEmpty()
                                    ) {
                                        AddITUserRoleField(
                                            value = role,
                                            expanded = roleExpanded,
                                            onToggleExpanded = {
                                                roleExpanded = !roleExpanded
                                            },
                                            onDismissExpanded = { roleExpanded = false },
                                            onSelected = {
                                                role = it
                                                roleExpanded = false
                                            },
                                            error = validation?.role.orEmpty()
                                        )
                                    }
                                }
                                Row(horizontalArrangement = Arrangement.spacedBy(24.dp)) {
                                    AddITUserFieldColumn(
                                        modifier = Modifier.weight(1f),
                                        label = "Mobile Number",
                                        required = true,
                                        error = validation?.mobile.orEmpty()
                                    ) {
                                        AddITUserTextField(
                                            value = mobileNumber,
                                            onValueChange = { mobileNumber = it },
                                            placeholder = "Enter mobile number",
                                            error = validation?.mobile.orEmpty(),
                                            keyboardType = KeyboardType.Phone
                                        )
                                    }
                                    AddITUserFieldColumn(
                                        modifier = Modifier.weight(1f),
                                        label = "Email Address",
                                        required = true,
                                        error = validation?.email.orEmpty()
                                    ) {
                                        AddITUserTextField(
                                            value = emailAddress,
                                            onValueChange = { emailAddress = it },
                                            placeholder = "Enter email address",
                                            error = validation?.email.orEmpty(),
                                            keyboardType = KeyboardType.Email
                                        )
                                    }
                                }
                            }
                        }
                    }
                }

                HorizontalDivider(color = ITUsersBorder)

                AddITUserFooter(
                    saveLabel = saveLabel,
                    onCancel = onClose,
                    onSave = {
                        hasSubmitted = true
                        val currentValues = ITUserFormValues(
                            name = fullName,
                            mobile = mobileNumber,
                            email = emailAddress,
                            role = role,
                            status = status
                        )
                        val errors = validateAddITUser(currentValues)
                        if (listOf(errors.name, errors.mobile, errors.email, errors.role, errors.status).none { it.isNotBlank() } && !effectiveSaving) {
                            localSaving = true
                            onSave(currentValues)
                        }
                    },
                    isSaving = effectiveSaving
                )
            }
        }
    }

    LaunchedEffect(isSaving) {
        if (!isSaving) {
            localSaving = false
        }
    }
}

@Composable
private fun AddITUserHeader(
    title: String,
    subtitle: String,
    onClose: () -> Unit
) {
    AppDialogHeader(
        title = title,
        subtitle = subtitle,
        icon = Res.drawable.user_plus,
        closeContentDescription = "Close add IT user dialog",
        onClose = onClose,
        colors = AddITUserDialogColors
    )
}

@Composable
private fun AddITUserFieldColumn(
    label: String,
    required: Boolean,
    modifier: Modifier = Modifier,
    error: String = "",
    field: @Composable () -> Unit
) {
    AppDialogFieldColumn(
        label = label,
        required = required,
        modifier = modifier,
        error = error,
        colors = AddITUserDialogColors,
        field = field
    )
}

@Composable
private fun AddITUserTextField(
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String,
    error: String,
    keyboardType: KeyboardType
) {
    when {
        placeholder.contains("full name", ignoreCase = true) -> AppDialogFullNameField(
            value = value,
            onValueChange = onValueChange,
            placeholder = placeholder,
            error = error,
            colors = AddITUserDialogColors
        )
        keyboardType == KeyboardType.Phone -> AppDialogMobileField(
            value = value,
            onValueChange = onValueChange,
            placeholder = placeholder,
            error = error,
            colors = AddITUserDialogColors
        )
        else -> AppDialogTextField(
            value = value,
            onValueChange = onValueChange,
            placeholder = placeholder,
            error = error,
            keyboardType = keyboardType,
            colors = AddITUserDialogColors
        )
    }
}

@Composable
private fun AddITUserRoleField(
    value: String,
    expanded: Boolean,
    onToggleExpanded: () -> Unit,
    onDismissExpanded: () -> Unit,
    onSelected: (String) -> Unit,
    error: String
) {
    val borderColor = when {
        expanded -> ITUsersPrimary
        error.isNotBlank() -> ITUsersDanger
        else -> ITUsersInputBorder
    }

    var anchorSize by remember { mutableStateOf(IntSize.Zero) }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .onGloballyPositioned { anchorSize = it.size }
    ) {
        Surface(
            modifier = Modifier
                .fillMaxWidth()
                .height(40.dp)
                .clickable(onClick = onToggleExpanded),
            shape = RoundedCornerShape(8.dp),
            color = AddITUserSurface,
            border = androidx.compose.foundation.BorderStroke(1.dp, borderColor)
        ) {
            Row(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(horizontal = 12.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Text(
                    text = if (value.isBlank()) "Select role" else value,
                    color = if (value.isBlank()) ITUsersMuted else ITUsersNavy,
                    fontSize = 14.sp,
                    fontWeight = FontWeight.Medium,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.weight(1f)
                )
                Icon(
                    painter = painterResource(Res.drawable.chevron_down),
                    contentDescription = null,
                    tint = ITUsersNavy,
                    modifier = Modifier.size(20.dp).rotate(if (expanded) 180f else 0f)
                )
            }
        }

        if (expanded) {
            Popup(
                popupPositionProvider = anchoredPopupPositionProvider(),
                properties = PopupProperties(
                    focusable = true,
                    dismissOnClickOutside = true,
                    dismissOnBackPress = true
                ),
                onDismissRequest = onDismissExpanded
            ) {
                Surface(
                    modifier = Modifier.width(with(LocalDensity.current) { anchorSize.width.toDp() }),
                    shape = RoundedCornerShape(10.dp),
                    color = AddITUserSurface,
                    shadowElevation = 12.dp,
                    tonalElevation = 0.dp,
                    border = androidx.compose.foundation.BorderStroke(1.dp, ITUsersBorder)
                ) {
                    Column(modifier = Modifier.padding(vertical = 8.dp)) {
                        addITUserRoleOptions.forEach { option ->
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .height(56.dp)
                                    .clickable {
                                        onSelected(option.label)
                                        onDismissExpanded()
                                    }
                                    .padding(horizontal = 18.dp),
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(12.dp)
                            ) {
                                Box(
                                    modifier = Modifier
                                        .size(36.dp)
                                        .background(option.tileColor, RoundedCornerShape(10.dp)),
                                    contentAlignment = Alignment.Center
                                ) {
                                    Icon(
                                        painter = painterResource(option.icon),
                                        contentDescription = null,
                                        tint = option.iconTint,
                                        modifier = Modifier.size(22.dp)
                                    )
                                }
                                Text(
                                    text = option.label,
                                    color = ITUsersNavy,
                                    fontSize = 14.sp,
                                    fontWeight = FontWeight.Medium
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

private fun anchoredPopupPositionProvider(): PopupPositionProvider {
    return object : PopupPositionProvider {
        override fun calculatePosition(
            anchorBounds: IntRect,
            windowSize: IntSize,
            layoutDirection: LayoutDirection,
            popupContentSize: IntSize
        ): IntOffset {
            val x = anchorBounds.left.coerceIn(0, (windowSize.width - popupContentSize.width).coerceAtLeast(0))
            val belowY = anchorBounds.bottom + 6
            val aboveY = anchorBounds.top - popupContentSize.height - 6
            val y = if (belowY + popupContentSize.height <= windowSize.height) belowY else aboveY.coerceAtLeast(0)
            return IntOffset(x, y)
        }
    }
}

@Composable
private fun AddITUserStatusField(
    value: String,
    expanded: Boolean,
    onToggleExpanded: () -> Unit,
    onDismissExpanded: () -> Unit,
    onSelected: (String) -> Unit,
    error: String
) {
    AppDialogSelectField(
        value = value,
        placeholder = "Select status",
        expanded = expanded,
        options = addITUserStatusOptions.map { it.label },
        onToggleExpanded = onToggleExpanded,
        onDismissExpanded = onDismissExpanded,
        onSelected = onSelected,
        error = error,
        colors = AddITUserDialogColors
    )
}

@Composable
private fun AddITUserFooter(
    saveLabel: String,
    onCancel: () -> Unit,
    onSave: () -> Unit,
    isSaving: Boolean
) {
    AppDialogFooter(
        actions = listOf(
            AppDialogFooterAction(label = "Cancel", onClick = onCancel, width = 108.dp, enabled = !isSaving),
            AppDialogFooterAction(
                label = if (isSaving) "Saving..." else saveLabel,
                onClick = onSave,
                style = AppDialogActionStyle.Primary,
                width = 108.dp,
                enabled = !isSaving
            )
        ),
        colors = AddITUserDialogColors
    )
}
